import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	AfterViewChecked,
	Output,
	ViewChild,
} from '@angular/core';
import moment from 'moment';
import { LayoutService } from '../../services/layout.service';
import { SessionService } from '../../services/session.service';
import { ScrollToDirective } from '../../directives/scroll-to.directive';
import { MatDialog } from '@angular/material/dialog';
import { IImagesViewModalData, ImagesViewModalComponent } from '../../modals/images-view-modal/images-view-modal.component';
import { IStandConversationMessage } from '../../services/planograms.service';
import { ServiceFeatures } from '../../models/serviceFeatures';

import { SHARED_MODULES } from '../../../shared.';
import { HasFeaturePipe } from '../../pipes/has-feature.pipe';
import { SanitizerPipe } from '../../pipes/sanitizer.pipe';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { ViewedDirective } from '../../directives/viewed.directive';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { LoaderComponent } from '../loader/loader.component';
import { PhButtonComponent } from '../ph-button/ph-button.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { UserLogoGenerateComponent } from '../user-logo-generate/user-logo-generate.component';
import { DateFormatService } from '../../services/date-format.service';
const TIME_FORMAT = 'HH:mm',
	IMAGE_TYPES = ['png', 'jpg', 'jpeg', 'svg', 'webp'];

@Component({
    selector: 'app-layout-history',
    templateUrl: './layout-history.component.html',
    styleUrls: ['./layout-history.component.scss'],
	imports: [
		SHARED_MODULES,
		HasFeaturePipe,
		SanitizerPipe,
		ClickOutsideDirective,
		InfiniteScrollDirective,
		ScrollToDirective,
		ViewedDirective,
		FaIconComponent,
		LoaderComponent,
		PhButtonComponent,
		PhIconComponent,
		PhIconWrapperComponent,
		UserLogoGenerateComponent
	],
    standalone: true
})
export class LayoutHistoryComponent implements OnInit, AfterViewChecked {

	@Input('scroll_to_message') scroll_to_message: number;
	@Input('standName') set setStandName(name: string) {
		if (this.standName !== name) {
			this.standName = name;
			if (this.open) this.scrollToBottom();
		}
	}
	standName: string;
	@Input('planogramId') planogramId: number;
	@Input('standEdit') standEdit: boolean = false;
	@Output('close') close: EventEmitter<any> = new EventEmitter<any>();
	@Output('scroll') scroll: EventEmitter<any> = new EventEmitter<any>();
	@Output('revert') revertEmitter: EventEmitter<number> = new EventEmitter<number>();
	@ViewChild('scrollContainer') scrollContainer: ElementRef;
	@ViewChild(ScrollToDirective) scrollToDirective;
	original_messages: any[];
	hasDemoAccess: boolean = false;
	messages: any[];
	open: boolean = false;
	showMessage: boolean = false;
	hideScrollBtn: boolean = false;
	users: any[];
	activeUsers: Array<number> = [];
	page: number = 1;
	scrollHeight: number;
	onTop: boolean = false;
	onBottom: boolean = false;
	firstInit: boolean = false;
	private pendingScrollToBottom: boolean = false;

	private previousScrollHeight: number;
	private previousScrollTop: number;
	private isScrollLoading: boolean = false;

	protected readonly features: typeof ServiceFeatures = ServiceFeatures;

	constructor(
		private dialog: MatDialog,
		private Layout: LayoutService,
		public session: SessionService,
		private dateFormatService: DateFormatService
	) {}

	@Input('open') set setLayoutState(val: boolean) {
		this.open = val;
	}

	@Input('users') set setUsers(data) {
		this.users = data;
		this.activeUsers = data.filter(user => user?.active);
	}

	@Input('messages') set setMessages(messageObj: any) {
		let newMessages = (messageObj.messages || []).filter(message => message.type !== 'conversation_message');
		const hadNoMessages = !this.original_messages?.length;
		const shouldMaintainScrollPosition = this.page !== messageObj.nextPage && this.messages && this.messages.length > 0;

		if (shouldMaintainScrollPosition && this.scrollContainer) {
			this.previousScrollHeight = this.scrollContainer.nativeElement.scrollHeight;
			this.previousScrollTop = this.scrollContainer.nativeElement.scrollTop;
			this.isScrollLoading = true;
		}

		if (this.original_messages && this.original_messages.length < newMessages.length && this.page === messageObj.nextPage) {
			this.showMessage = true;
			this.onBottom = this.scrollOnBottom();
		} else if (this.original_messages && this.original_messages.length === newMessages.length) {
			this.showMessage = false;
		}

		this.original_messages = newMessages;

		newMessages.forEach(LayoutHistoryComponent.formatTime);

		const sortedAllMessages = [...newMessages].sort((a, b) => {
			const a_d = new Date(a.created_at);
			const b_d = new Date(b.created_at);
			return +b_d - +a_d;
		});

		const groupedMessages = [];

		sortedAllMessages.forEach(message => {
			const messageDate = moment(message.created_at);
			let groupType: string;

			if (messageDate.isSame(moment(), 'day')) {
				groupType = 'TODAY';
			} else if (messageDate.isSame(moment().subtract(1, 'day'), 'day')) {
				groupType = 'YESTERDAY';
			} else {
				groupType = this.dateFormatService.formatDate(message.created_at, 'date');
			}

			let existingGroup = groupedMessages.find(group => group.type === groupType);
			if (!existingGroup) {
				existingGroup = { type: groupType, results: [] };
				groupedMessages.push(existingGroup);
			}
			existingGroup.results.push(message);
		});

		groupedMessages.forEach(group => {
			group.results.forEach(item => {
				item.from_user_name = item.user?.full_name ?? 'User';

				if (!item.conf) return;

				if (item.conf.to_user) {
					item.to_user_name = (this.users.find(i => i.id === item.conf.to_user) || {}).full_name;
				}

				if (item.conf.shop_name) {
					item.shop_name = item.conf.shop_name;
				}

				if ((item.users || []).length) {
					item.to_users = item.users
						.map(i => i.full_name)
						.join(', ');
				}
			});
		});

		this.page = messageObj.nextPage;
		this.messages = groupedMessages.reverse();

		this.messages.forEach(m => {
			m.results = m.results.sort((a, b) => {
				const a_d = new Date(a.created_at);
				const b_d = new Date(b.created_at);
				return +a_d - +b_d;
			});
		});

		if (this.open && !shouldMaintainScrollPosition && hadNoMessages && newMessages.length > 0) {
			this.scrollToBottom();
		}
	}

	scrollToBottom(): void {
		this.pendingScrollToBottom = true;
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				if (this.pendingScrollToBottom) {
					this.performScrollToBottom();
				}
			});
		});
	}

	static formatTime(i) {
		if (!i.created_at) {
			i.created_at = new Date();
		}

		i.time = moment(i.created_at).format(TIME_FORMAT);
	}

	static shouldUpdateMessageReadStatus(message: IStandConversationMessage, id: number): boolean {
		return message.users.map(u => u.id).includes(id)
			&& !message.read && message.user?.id != id;
	}

	ngOnInit() {
		if (this.scroll_to_message) this.open = true;
		this.hasDemoAccess = this.session.hasFeature(ServiceFeatures.Demo);
		this.firstInit = true;
	}

	ngAfterViewChecked() {
		if (this.scrollContainer) {
			if (this.isScrollLoading) {
				const newScrollHeight = this.scrollContainer.nativeElement.scrollHeight;
				const scrollHeightDifference = newScrollHeight - this.previousScrollHeight;
				this.scrollContainer.nativeElement.scrollTop = this.previousScrollTop + scrollHeightDifference;
				this.isScrollLoading = false;
				this.onTop = false;
			}

			if (this.pendingScrollToBottom && this.original_messages?.length) {
				this.performScrollToBottom();
			}

			if (this.onBottom && this.showMessage) {
				this.scrollToDirective.scroll(this.scrollContainer.nativeElement.scrollHeight);
				this.onBottom = false;
			}
		}
	}

	private performScrollToBottom(): void {
		if (!this.scrollContainer) return;

		const container = this.scrollContainer.nativeElement;

		if (this.scrollToDirective) {
			this.scrollToDirective.scroll(container.scrollHeight);
		} else {
			container.scrollTop = container.scrollHeight;
		}

		this.pendingScrollToBottom = false;
		this.firstInit = false;
	}

	closePanel() {
		this.open = false;
		this.pendingScrollToBottom = false;
		this.firstInit = true;
		this.onTop = false;
		this.close.emit(false);
	}

	showFullImg(msg, indexFile) {
		const filesArray = msg.files.map(item => item.file);

		this.dialog
			.open<ImagesViewModalComponent, IImagesViewModalData>(ImagesViewModalComponent, {
				width: 'auto',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					images: filesArray.filter(i => this.isPhoto(i)),
					index: indexFile,
					showMainButton: msg.stand_version && ['CHANGE_LAYOUT', 'REVERT_STAND_VERSION'].includes(msg.conf.status) && this.standEdit,
					showSecondaryButton: true,
					mainButtonText: 'REVERT_STAND',
					secondaryButtonText: 'COMMON.CLOSE',
					changedVersion: ['CHANGE_LAYOUT', 'REVERT_STAND_VERSION'].includes(msg.conf.status),
					username: msg.from_user_name,
					date: msg.created_at
				}
			})
			.afterClosed().subscribe((result: string) => {
				if (result === 'main') this.revertStandEmit(msg.id);
		});
	}

	downloadFile(path) {
		const a = document.createElement('a'),
			decodedPath = decodeURIComponent(path);

		a.href = decodedPath;
		a.target = '_blank';
		a.download = decodedPath;
		a.style.display = 'none';

		document.body.appendChild(a);

		a.click();
		a.remove();
	}

	scrollUp() {
		this.scroll.emit();
	}

	removeAttachment(msg, f) {
		msg.files = msg.files.filter(i => i != f);
	}

	isPhoto(fname) {
		return IMAGE_TYPES.includes((fname.split('?')[0].split('.').slice(-1)[0] || '').toLowerCase());
	}

	showNewMessages() {
		if (this.scrollContainer) {
			this.scrollToDirective.scroll(this.scrollContainer.nativeElement.scrollHeight);
		}
		this.onBottom = true;
		this.hideScrollBtn = true;
		this.showMessage = false;
	}

	hideButton(value) {
		this.hideScrollBtn = value;
	}

	setMessageReadStatus(id) {
		const message = this.original_messages.find(i => i.id == id);

		if (!message) return;
		if (LayoutHistoryComponent.shouldUpdateMessageReadStatus(message, this.session.defaults.user_id)) {
			message.read = true;
			return this.Layout.updateMessageStatus(message.id, { read: true });
		}
	}

	async updateMessageStatus(array) {
		await Promise.all(array.map(item => this.setMessageReadStatus(item)));
	}

	scrollOnBottom() {
		if (!this.scrollContainer) return false;
		const container = this.scrollContainer.nativeElement;
		const tolerance = 1;
		return (container.scrollHeight - container.scrollTop <= container.offsetHeight + tolerance);
	}

	revertStandEmit(id: number): void {
		this.revertEmitter.emit(id);
	}

	protected trackProduct(index: number, product: any) {
		return product?.id
			?? product?.article
			?? product?.product_rotate_from?.article
			?? product?.product_rotate_to?.article
			?? index;
	}
}
